import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Avatar,
  ListItemAvatar,
  useTheme,
  useMediaQuery,
  Grid,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  Autocomplete,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Event as EventIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  ViewModule as ViewModuleIcon,
  ViewWeek as ViewWeekIcon,
} from "@mui/icons-material";
import axios from "axios";
import { getLessonTypeStyle } from "../../utils/colorUtils";
import { getAuthHeaders } from "../../utils/auth";
import "../../assets/style/Timetable.css";

const ScheduledLessonManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonParticipants, setLessonParticipants] = useState([]);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [weekOffset, setWeekOffset] = useState(0);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [editedStartTime, setEditedStartTime] = useState('');

  // States for creating new lesson
  const [createLessonDialogOpen, setCreateLessonDialogOpen] = useState(false);
  const [lessonTypes, setLessonTypes] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [events, setEvents] = useState([]);
  const [isCreatingLesson, setIsCreatingLesson] = useState(false);
  const [newLessonData, setNewLessonData] = useState({
    lesson_type_id: '',
    trainer_id: '',
    start: '',
    duration_minutes: 50,
    event_id: '',
    is_free: false,
    payment_on_site: false,
    payment_only_online: false,
    online_payment_amount: null,
    on_site_payment_amount: null
  });

  const scheduleEndpoint = `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson_schedule/?mode=hour_by_hour&flat=False&week_offset=${weekOffset}`;

  useEffect(() => {
    fetchSchedule();
  }, [weekOffset]);

  // Fetch lesson types, trainers, and events when dialog opens (lazy loading)
  useEffect(() => {
    if (createLessonDialogOpen && lessonTypes.length === 0) {
      fetchLessonTypes();
    }
    if (createLessonDialogOpen && trainers.length === 0) {
      fetchTrainers();
    }
    if (createLessonDialogOpen && events.length === 0) {
      fetchEvents();
    }
  }, [createLessonDialogOpen]);

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      const response = await axios.get(scheduleEndpoint);
      setSchedule(response.data);
      setError('');
    } catch (error) {
      console.error("Error fetching schedule:", error);
      setError('Nie udało się pobrać grafiku zajęć');
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonTypes = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type/`,
        { headers }
      );
      if (response.status === 200 && Array.isArray(response.data.objects)) {
        setLessonTypes(response.data.objects);
      }
    } catch (error) {
      console.error("Error fetching lesson types:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do pobrania typów zajęć");
      }
    }
  };

  const fetchTrainers = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/trainers/`,
        { headers }
      );
      if (response.status === 200) {
        setTrainers(Array.isArray(response.data) ? response.data : response.data.objects || []);
      }
    } catch (error) {
      console.error("Error fetching trainers:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do pobrania trenerów");
      }
      setTrainers([]);
    }
  };

  const fetchEvents = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/events`,
        { headers }
      );
      if (response.status === 200) {
        // Filter only active events
        const activeEvents = (Array.isArray(response.data) ? response.data : response.data.objects || [])
          .filter(event => event.active);
        setEvents(activeEvents);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do pobrania wydarzeń");
      }
      setEvents([]);
    }
  };

  const fetchLessonParticipants = async (lessonId) => {
    setIsLoadingParticipants(true);
    try {
      // Use optimized endpoint that fetches only the specific lesson's attendees
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson/${lessonId}/attendees`,
        { headers }
      );

      setLessonParticipants(response.data.attendees || []);
    } catch (error) {
      console.error("Error fetching lesson participants:", error);
      if (error.response && (error.response.status === 403 || error.response.status === 401)) {
        setError('Brak uprawnień do pobrania uczestników zajęć');
      }
      setLessonParticipants([]);
    } finally {
      setIsLoadingParticipants(false);
    }
  };

  const handleDeleteLesson = async () => {
    if (!selectedLesson) return;

    try {
      setIsDeleting(true);
      const headers = await getAuthHeaders();
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson/${selectedLesson.lesson_id}`,
        { headers }
      );

      if (response.status === 204) {
        setSuccess('Zajęcia zostały pomyślnie usunięte. Wszyscy uczestnicy zostali powiadomieni.');
        setDeleteDialogOpen(false);
        setSelectedLesson(null);
        setLessonParticipants([]);
        await fetchSchedule();
      }
    } catch (error) {
      console.error("Error deleting lesson:", error);
      if (error.response && (error.response.status === 403 || error.response.status === 401)) {
        setError('Brak uprawnień do usunięcia zajęć');
      } else if (error.response?.data?.detail) {
        setError(`Nie udało się usunąć zajęć: ${error.response.data.detail}`);
      } else {
        setError('Nie udało się usunąć zajęć');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenDeleteDialog = async (lesson) => {
    setSelectedLesson(lesson);
    await fetchLessonParticipants(lesson.lesson_id);
    setDeleteDialogOpen(true);
  };

  const handleOpenEditDialog = async (lesson) => {
    setSelectedLesson(lesson);
    await fetchLessonParticipants(lesson.lesson_id);

    // Format the current start time for the datetime-local input
    const { start } = lesson;
    const dateStr = `${start.year}-${String(start.month).padStart(2, '0')}-${String(start.day).padStart(2, '0')}`;
    const timeStr = `${String(start.hour).padStart(2, '0')}:${String(start.minute).padStart(2, '0')}`;
    setEditedStartTime(`${dateStr}T${timeStr}`);

    setEditDialogOpen(true);
  };

  const handleUpdateLesson = async () => {
    if (!selectedLesson || !editedStartTime) return;

    try {
      setIsUpdating(true);

      // Send datetime-local value directly without timezone conversion
      // The backend expects datetime in local timezone (Poland)
      // datetime-local input gives us "2026-01-26T19:00" format
      // Add seconds to match backend's expected format: "2026-01-26T19:00:00"
      const localDateTime = editedStartTime + ':00';

      const headers = await getAuthHeaders();
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson/${selectedLesson.lesson_id}`,
        { start: localDateTime },
        { headers }
      );

      if (response.status === 200) {
        setSuccess('Godzina zajęć została pomyślnie zmieniona. Wszyscy uczestnicy zostali powiadomieni.');
        setEditDialogOpen(false);
        setSelectedLesson(null);
        setLessonParticipants([]);
        setEditedStartTime('');
        await fetchSchedule();
      }
    } catch (error) {
      console.error("Error updating lesson:", error);
      if (error.response && (error.response.status === 403 || error.response.status === 401)) {
        setError('Brak uprawnień do aktualizacji zajęć');
      } else if (error.response?.data?.detail) {
        setError(`Nie udało się zaktualizować zajęć: ${error.response.data.detail}`);
      } else {
        setError('Nie udało się zaktualizować zajęć');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCreateLesson = async () => {
    try {
      setIsCreatingLesson(true);
      setError('');

      // Convert duration from minutes to timedelta format (HH:MM:SS)
      const hours = Math.floor(newLessonData.duration_minutes / 60);
      const minutes = newLessonData.duration_minutes % 60;
      const durationString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;

      // Add seconds to datetime
      const startDateTime = newLessonData.start + ':00';

      const payload = {
        lesson_type_id: parseInt(newLessonData.lesson_type_id),
        trainer_id: parseInt(newLessonData.trainer_id),
        start: startDateTime,
        duration: durationString,
        recurrent: false, // Single lesson, not recurring
        event_id: newLessonData.event_id ? parseInt(newLessonData.event_id) : null,
        is_free: newLessonData.is_free,
        payment_on_site: newLessonData.payment_on_site,
        payment_only_online: newLessonData.payment_only_online,
        online_payment_amount: newLessonData.online_payment_amount,
        on_site_payment_amount: newLessonData.on_site_payment_amount
      };

      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson/`,
        payload,
        { headers }
      );

      if (response.status === 201) {
        setSuccess('Zajęcia zostały pomyślnie dodane do grafiku!');
        setCreateLessonDialogOpen(false);
        setNewLessonData({
          lesson_type_id: '',
          trainer_id: '',
          start: '',
          duration_minutes: 50,
          event_id: '',
          is_free: false,
          payment_on_site: false,
          payment_only_online: false,
          online_payment_amount: null,
          on_site_payment_amount: null
        });
        await fetchSchedule();
      }
    } catch (error) {
      console.error("Error creating lesson:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Brak uprawnień do utworzenia zajęć");
      } else if (error.response?.data?.detail) {
        setError(`Nie udało się utworzyć zajęć: ${error.response.data.detail}`);
      } else {
        setError('Nie udało się utworzyć zajęć');
      }
    } finally {
      setIsCreatingLesson(false);
    }
  };

  const formatDateTime = (lesson) => {
    // Handle both lesson data formats
    if (lesson.week_day) {
      // Timetable format
      return lesson.week_day;
    }
    // Legacy format
    const { start } = lesson;
    const dayNames = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
    const monthNames = ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
                       'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'];

    const date = new Date(start.year, start.month - 1, start.day);
    const dayName = dayNames[date.getDay()];
    const monthName = monthNames[start.month - 1];

    return `${dayName}, ${start.day} ${monthName} ${start.year}`;
  };

  const formatTime = (lesson) => {
    const { start, end } = lesson;
    return `${start.hour.toString().padStart(2, '0')}:${start.minute.toString().padStart(2, '0')} - ${end.hour.toString().padStart(2, '0')}:${end.minute.toString().padStart(2, '0')}`;
  };

  // Component to render lessons as cards (mobile-friendly)
  const LessonCardsView = () => {
    const timetable = organizeScheduledLessonsForTimetable();
    const weekDayMap = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
    const addLeadingZeros = (num) => String(num).padStart(2, "0");

    // Organize lessons by day
    const lessonsByDay = {};
    weekDayMap.forEach(day => {
      lessonsByDay[day] = [];
    });

    Object.keys(timetable).forEach(timeSlot => {
      const lessonsAtTime = timetable[timeSlot];
      if (lessonsAtTime && Array.isArray(lessonsAtTime)) {
        lessonsAtTime.forEach(lesson => {
          if (lesson.week_day && lessonsByDay[lesson.week_day]) {
            lessonsByDay[lesson.week_day].push(lesson);
          }
        });
      }
    });

    return (
      <Box>
        {weekDayMap.map(day => {
          const dayLessons = lessonsByDay[day];
          if (dayLessons.length === 0) return null;

          return (
            <Box key={day} sx={{ mb: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  pb: 1, 
                  borderBottom: '2px solid #667eea',
                  fontWeight: 600,
                  color: '#667eea'
                }}
              >
                {day}
              </Typography>
              <Grid container spacing={2}>
                {dayLessons.map((lesson) => {
                  const start = `${addLeadingZeros(lesson.start.hour)}:${addLeadingZeros(lesson.start.minute)}`;
                  const end = `${addLeadingZeros(lesson.end.hour)}:${addLeadingZeros(lesson.end.minute)}`;
                  const maxPeople = lesson.lesson_type?.max_people || 'Brak limitu';
                  const currentPeople = lesson.current_people || 0;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={lesson.lesson_id}>
                      <Card 
                        sx={{ 
                          height: '100%',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-2px)',
                          }
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.9rem',
                              textAlign: 'center',
                              mb: 1.5,
                              ...getLessonTypeStyle(lesson.lesson_type?.bg_color),
                            }}
                          >
                            {lesson.lesson_type?.friendly_name || lesson.lesson_type?.name}
                          </Box>

                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <TimeIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight={500}>
                              {start} - {end}
                            </Typography>
                          </Box>

                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              Trener: {lesson.trainer}
                            </Typography>
                          </Box>

                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <GroupIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              Zapisanych: {currentPeople}/{maxPeople}
                            </Typography>
                          </Box>

                          {lesson.waitlist_count > 0 && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block', 
                                mb: 1.5, 
                                color: 'warning.main', 
                                fontWeight: 500 
                              }}
                            >
                              Na liście oczekujących: {lesson.waitlist_count}
                            </Typography>
                          )}

                          <Box display="flex" gap={1} mt={1}>
                            <Button
                              fullWidth
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditDialog(lesson);
                              }}
                              startIcon={<EditIcon />}
                              color="primary"
                              variant="outlined"
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                  color: 'white'
                                }
                              }}
                            >
                              Edytuj
                            </Button>
                            <Button
                              fullWidth
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDeleteDialog(lesson);
                              }}
                              startIcon={<DeleteIcon />}
                              color="error"
                              variant="outlined"
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'error.light',
                                  color: 'white'
                                }
                              }}
                            >
                              Usuń
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          );
        })}
      </Box>
    );
  };

  // Component to render a single hour row in the scheduled lessons timetable
  const SingleScheduledLessonHour = ({ hour, timeSlot }) => {
    const weekDayMap = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
    const timeSlots = [];

    const addLeadingZeros = (num) => String(num).padStart(2, "0");

    for (let i = 0; i < 7; i++) {
      const dayName = weekDayMap[i];
      const lessonsInDay = hour ? hour.filter(lesson => lesson.week_day === dayName) : [];

      if (lessonsInDay.length > 0) {
        const lessonGroup = lessonsInDay.map((lesson) => {
          const start = `${addLeadingZeros(lesson.start.hour)}:${addLeadingZeros(lesson.start.minute)}`;
          const end = `${addLeadingZeros(lesson.end.hour)}:${addLeadingZeros(lesson.end.minute)}`;
          const maxPeople = lesson.lesson_type?.max_people || 'Brak limitu';
          const currentPeople = lesson.current_people || 0;

          return (
            <Box
              key={lesson.lesson_id}
              sx={{
                p: 2,
                mb: 1,
                borderRadius: 2,
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease'
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  mb: 1,
                  ...getLessonTypeStyle(lesson.lesson_type?.bg_color),
                }}
              >
                {lesson.lesson_type?.friendly_name || lesson.lesson_type?.name}
              </Box>

              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, textAlign: 'center' }}>
                {start} - {end}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
                Trener: {lesson.trainer}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 0.5 }}>
                Zapisanych: {currentPeople}/{maxPeople}
              </Typography>

              {lesson.waitlist_count > 0 && (
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1, color: 'warning.main', fontWeight: 500 }}>
                  Na liście oczekujących: {lesson.waitlist_count}
                </Typography>
              )}

              <Box display="flex" gap={0.5} flexWrap="wrap" justifyContent="center" sx={{ mt: lesson.waitlist_count > 0 ? 0.5 : 1.5 }}>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditDialog(lesson);
                  }}
                  startIcon={<EditIcon />}
                  color="primary"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    px: 1,
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'white'
                    }
                  }}
                >
                  Edytuj
                </Button>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDeleteDialog(lesson);
                  }}
                  startIcon={<DeleteIcon />}
                  color="error"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    px: 1,
                    '&:hover': {
                      backgroundColor: 'error.light',
                      color: 'white'
                    }
                  }}
                >
                  Usuń
                </Button>
              </Box>
            </Box>
          );
        });

        timeSlots.push(
          <td key={`lesson-group-${i}`} style={{ padding: '16px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
            {lessonGroup}
          </td>
        );
      } else {
        timeSlots.push(
          <td
            key={`empty-${i}`}
            style={{
              backgroundColor: '#f8f9fa',
              height: '120px',
              border: '1px solid #e0e0e0'
            }}
          />
        );
      }
    }

    return <tr>{timeSlots}</tr>;
  };

  // Helper function to organize scheduled lessons into timetable structure
  const organizeScheduledLessonsForTimetable = () => {
    const timetable = {};

    if (schedule && schedule.objects) {
      Object.keys(schedule.objects).forEach(timeKey => {
        if (Array.isArray(schedule.objects[timeKey])) {
          timetable[timeKey] = schedule.objects[timeKey];
        }
      });
    }

    return timetable;
  };

  const getLocationInfo = (lesson) => {
    if (lesson.lesson_type?.location?.name) {
      return lesson.lesson_type.location.name;
    }
    return 'Brak informacji o lokalizacji';
  };

  const getTrainerInfo = (lesson) => {
    return lesson.trainer?.full_name || lesson.trainer || 'Brak informacji o trenerze';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <Box textAlign="center">
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">
            Ładowanie grafiku zajęć...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={2}>
          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              Zarządzanie Zaplanowanymi Zajęciami
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Przeglądaj i usuwaj zaplanowane zajęcia. Uczestnicy zostaną automatycznie powiadomieni o zmianach.
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Button
              onClick={() => setCreateLessonDialogOpen(true)}
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                textTransform: 'none',
                whiteSpace: 'nowrap'
              }}
            >
              Dodaj zajęcia
            </Button>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              <ToggleButton value="table" aria-label="table view">
                <ViewWeekIcon sx={{ mr: 0.5 }} />
                Tabela
              </ToggleButton>
              <ToggleButton value="cards" aria-label="cards view">
                <ViewModuleIcon sx={{ mr: 0.5 }} />
                Karty
              </ToggleButton>
            </ToggleButtonGroup>
            <IconButton onClick={fetchSchedule} color="primary" size={isMobile ? "medium" : "large"}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Week Navigation */}
        <Box 
          display="flex" 
          gap={{ xs: 1, sm: 2 }} 
          flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
          justifyContent={{ xs: 'stretch', sm: 'flex-start' }}
        >
          <Button
            onClick={() => setWeekOffset(weekOffset - 1)}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' }, minWidth: { xs: 'auto', sm: '120px' } }}
          >
            {isMobile ? 'Poprzedni' : 'Poprzedni tydzień'}
          </Button>
          <Button
            onClick={() => setWeekOffset(0)}
            variant="outlined"
            disabled={weekOffset === 0}
            size={isMobile ? "small" : "medium"}
            sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' }, minWidth: { xs: 'auto', sm: '120px' } }}
          >
            {isMobile ? 'Obecny' : 'Obecny tydzień'}
          </Button>
          <Button
            onClick={() => setWeekOffset(weekOffset + 1)}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' }, minWidth: { xs: 'auto', sm: '120px' } }}
          >
            {isMobile ? 'Następny' : 'Następny tydzień'}
          </Button>
        </Box>
      </Box>

      {/* Success/Error Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Timetable View */}
      {Object.keys(organizeScheduledLessonsForTimetable()).length > 0 ? (
        viewMode === 'cards' || isMobile ? (
          <LessonCardsView />
        ) : (
          <Box sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: 'auto',
            backgroundColor: 'white'
          }}>
            <div id="timetable">
              <div className="table-responsive" style={{ overflowX: 'auto' }}>
                <table
                  className="table table-bordered text-center white-table"
                  style={{
                    marginBottom: 0,
                    minWidth: '800px'
                  }}
                >
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th className="text-uppercase" style={{
                      padding: { xs: '8px', sm: '16px' },
                      fontWeight: 600,
                      color: '#333',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Poniedziałek
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        PON
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: { xs: '8px', sm: '16px' },
                      fontWeight: 600,
                      color: '#333',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Wtorek
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        WT
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: { xs: '8px', sm: '16px' },
                      fontWeight: 600,
                      color: '#333',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Środa
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        ŚR
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: { xs: '8px', sm: '16px' },
                      fontWeight: 600,
                      color: '#333',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Czwartek
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        CZ
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: { xs: '8px', sm: '16px' },
                      fontWeight: 600,
                      color: '#333',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Piątek
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        PT
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: { xs: '8px', sm: '16px' },
                      fontWeight: 600,
                      color: '#333',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Sobota
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        SO
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: { xs: '8px', sm: '16px' },
                      fontWeight: 600,
                      color: '#333',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Niedziela
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        ND
                      </Box>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(organizeScheduledLessonsForTimetable())
                    .sort((a, b) => {
                      const [hourA, minuteA] = a.split(':').map(Number);
                      const [hourB, minuteB] = b.split(':').map(Number);

                      if (hourA !== hourB) {
                        return hourA - hourB;
                      }
                      return minuteA - minuteB;
                    })
                    .map((timeSlot) => {
                      const lessonsAtTime = organizeScheduledLessonsForTimetable()[timeSlot];
                      return (
                        <SingleScheduledLessonHour
                          key={timeSlot}
                          hour={lessonsAtTime}
                          timeSlot={timeSlot}
                        />
                      );
                    })}
                </tbody>
                </table>
              </div>
            </div>
          </Box>
        )
      ) : (
        <Box sx={{
          p: 6,
          textAlign: 'center',
          border: '2px dashed #e0e0e0',
          borderRadius: 2,
          backgroundColor: '#fafafa'
        }}>
          <AddIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Brak zaplanowanych zajęć
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            W tym tygodniu nie ma żadnych zaplanowanych zajęć.
          </Typography>
        </Box>
      )}

      {/* Edit Lesson Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon color="primary" />
            <Typography variant="h6">Edytuj godzinę zajęć</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLesson && (
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Zmień godzinę zajęć
              </Typography>

              <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <EventIcon color="primary" />
                  <Typography variant="body1" fontWeight={500}>
                    {formatDateTime(selectedLesson)}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Chip
                    label={selectedLesson.lesson_type?.friendly_name || selectedLesson.lesson_type?.name || 'Nieznany typ'}
                    size="small"
                    sx={{
                      backgroundColor: selectedLesson.lesson_type?.bg_color || '#default',
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </Box>

                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PersonIcon color="primary" />
                  <Typography variant="body1">
                    Trener: {getTrainerInfo(selectedLesson)}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <LocationIcon color="primary" />
                  <Typography variant="body1">
                    Lokalizacja: {getLocationInfo(selectedLesson)}
                  </Typography>
                </Box>
              </Paper>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Nowa data i godzina
              </Typography>

              <Box sx={{ mb: 3 }}>
                <input
                  type="datetime-local"
                  value={editedStartTime}
                  onChange={(e) => setEditedStartTime(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontFamily: 'inherit'
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Zapisani uczestnicy ({lessonParticipants.length}):
              </Typography>

              {isLoadingParticipants ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <CircularProgress size={40} />
                </Box>
              ) : lessonParticipants.length > 0 ? (
                <List sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  {lessonParticipants.map((participant, index) => (
                    <ListItem key={index} dense>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={participant.name}
                        secondary={participant.email || participant.phone}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Brak zapisanych uczestników
                </Typography>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Uwaga:</strong> Wszyscy uczestnicy zostaną automatycznie powiadomieni o zmianie godziny zajęć.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            color="primary"
            disabled={isUpdating}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleUpdateLesson}
            color="primary"
            variant="contained"
            disabled={isUpdating || !editedStartTime}
            startIcon={isUpdating ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {isUpdating ? 'Zapisywanie...' : 'Zapisz zmiany'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Lesson Dialog */}
      <Dialog
        open={createLessonDialogOpen}
        onClose={() => setCreateLessonDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AddIcon color="primary" />
            <Typography variant="h6">Dodaj zajęcia do grafiku</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              options={lessonTypes.sort((a, b) => {
                const nameA = a.friendly_name || a.name;
                const nameB = b.friendly_name || b.name;
                return nameA.localeCompare(nameB);
              })}
              getOptionLabel={(option) => option.friendly_name || option.name}
              value={lessonTypes.find(lt => lt.id === newLessonData.lesson_type_id) || null}
              onChange={(e, newValue) => setNewLessonData(prev => ({ 
                ...prev, 
                lesson_type_id: newValue?.id || '' 
              }))}
              renderInput={(params) => <TextField {...params} label="Typ zajęć" />}
              fullWidth
              sx={{ mb: 2 }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <Autocomplete
              options={trainers.sort((a, b) => a.full_name.localeCompare(b.full_name))}
              getOptionLabel={(option) => option.full_name}
              value={trainers.find(t => t.id === newLessonData.trainer_id) || null}
              onChange={(e, newValue) => setNewLessonData(prev => ({ 
                ...prev, 
                trainer_id: newValue?.id || '' 
              }))}
              renderInput={(params) => <TextField {...params} label="Trener" />}
              fullWidth
              sx={{ mb: 2 }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <TextField
              label="Data i godzina rozpoczęcia"
              type="datetime-local"
              value={newLessonData.start}
              onChange={(e) => setNewLessonData(prev => ({ ...prev, start: e.target.value }))}
              fullWidth
              sx={{ mb: 2 }}
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              label="Długość zajęć (minuty)"
              type="number"
              value={newLessonData.duration_minutes}
              onChange={(e) => setNewLessonData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 50 }))}
              fullWidth
              sx={{ mb: 2 }}
              inputProps={{
                min: 1,
                step: 5
              }}
              helperText="Wprowadź długość zajęć w minutach (np. 50, 60, 90)"
            />

            <Autocomplete
              options={[{ id: '', name: 'Brak wydarzenia' }, ...events.sort((a, b) => a.name.localeCompare(b.name))]}
              getOptionLabel={(option) => option.name}
              value={events.find(e => e.id === newLessonData.event_id) || { id: '', name: 'Brak wydarzenia' }}
              onChange={(e, newValue) => setNewLessonData(prev => ({ 
                ...prev, 
                event_id: newValue?.id || '' 
              }))}
              renderInput={(params) => <TextField {...params} label="Wydarzenie (opcjonalnie)" />}
              fullWidth
              sx={{ mb: 2 }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Opcje płatności:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newLessonData.is_free}
                  onChange={(e) => setNewLessonData(prev => ({ ...prev, is_free: e.target.checked }))}
                />
              }
              label="Zajęcia bezpłatne"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newLessonData.payment_on_site}
                  onChange={(e) => setNewLessonData(prev => ({ ...prev, payment_on_site: e.target.checked }))}
                />
              }
              label="Płatność tylko na miejscu"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newLessonData.payment_only_online}
                  onChange={(e) => setNewLessonData(prev => ({ ...prev, payment_only_online: e.target.checked }))}
                />
              }
              label="Tylko płatność online"
            />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Płatność podzielona (opcjonalnie):
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Jeśli chcesz, aby użytkownicy płacili część online, a część na miejscu, wypełnij oba pola poniżej.
              </Typography>
              <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                <TextField
                  label="Kwota online (PLN)"
                  type="number"
                  value={newLessonData.online_payment_amount || ''}
                  onChange={(e) => setNewLessonData(prev => ({ ...prev, online_payment_amount: e.target.value ? parseInt(e.target.value) : null }))}
                  fullWidth
                  inputProps={{
                    min: 20,
                    step: 1
                  }}
                  helperText="Min. 20 PLN"
                />
                <TextField
                  label="Kwota na miejscu (PLN)"
                  type="number"
                  value={newLessonData.on_site_payment_amount || ''}
                  onChange={(e) => setNewLessonData(prev => ({ ...prev, on_site_payment_amount: e.target.value ? parseInt(e.target.value) : null }))}
                  fullWidth
                  inputProps={{
                    min: 1,
                    step: 1
                  }}
                  helperText="Min. 1 PLN"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateLessonDialogOpen(false)}
            color="primary"
            disabled={isCreatingLesson}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleCreateLesson}
            color="primary"
            variant="contained"
            disabled={isCreatingLesson || !newLessonData.lesson_type_id || !newLessonData.trainer_id || !newLessonData.start}
            startIcon={isCreatingLesson ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {isCreatingLesson ? 'Tworzenie...' : 'Dodaj zajęcia'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="error" />
            <Typography variant="h6">Usuń zajęcia</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLesson && (
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Czy na pewno chcesz usunąć te zajęcia?
              </Typography>

              <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <EventIcon color="primary" />
                  <Typography variant="body1" fontWeight={500}>
                    {formatDateTime(selectedLesson)}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <TimeIcon color="primary" />
                  <Typography variant="body1">
                    {formatTime(selectedLesson)}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Chip
                    label={selectedLesson.lesson_type?.friendly_name || selectedLesson.lesson_type?.name || 'Nieznany typ'}
                    size="small"
                    sx={{
                      backgroundColor: selectedLesson.lesson_type?.bg_color || '#default',
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </Box>

                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PersonIcon color="primary" />
                  <Typography variant="body1">
                    Trener: {getTrainerInfo(selectedLesson)}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <LocationIcon color="primary" />
                  <Typography variant="body1">
                    Lokalizacja: {getLocationInfo(selectedLesson)}
                  </Typography>
                </Box>
              </Paper>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Zapisani uczestnicy ({lessonParticipants.length}):
              </Typography>

              {isLoadingParticipants ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <CircularProgress size={40} />
                </Box>
              ) : lessonParticipants.length > 0 ? (
                <List sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  {lessonParticipants.map((participant, index) => (
                    <ListItem key={index} dense>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={participant.name}
                        secondary={participant.email || participant.phone}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Brak zapisanych uczestników
                </Typography>
              )}

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Uwaga:</strong> Usunięcie zajęć jest nieodwracalne. Wszyscy uczestnicy zostaną automatycznie powiadomieni o odwołaniu zajęć, a lekcje zostaną zwrócone na ich karnety (jeśli dotyczy).
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
            disabled={isDeleting}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleDeleteLesson}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Usuwanie...' : 'Usuń zajęcia'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledLessonManagement;
